import React from "react";
import { Button, Tooltip } from "@mui/material";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useHistory } from "react-router-dom";
import {
  JOB_TYPES,
  LIBRARY_MODAL_KEYS,
  MODULE_STATES,
} from "src/storage/constants";
import useGlobalStore from "src/store/useGlobalStore";
import styled from "styled-components";
import NoImage from "../../../../assets/images/empty-small.jpg";
import { getSrcTwo } from "../../../../storage/helpers";
import { CmPointerContainer, MpPointerContainer } from "../../styles";

const Wrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  margin: 0px;
  gap: 8px;
`;

const TextContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  min-width: 220px;

  height: 46px;
  flex-basis: 220px;
`;

const TitleSpan = styled.p`
  font-size: 12px;
  font-weight: 500;
  max-width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: black;
  overflow: hidden;

  &:hover {
    cursor: pointer;
  }
`;

const ParentSpan = styled.p`
  color: darkgrey;
  font-size: 10px;
  font-weight: 400;
  text-transform: uppercase;
  white-space: nowrap;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ModuleImage = styled(LazyLoadImage)`
  background-color: white;
  border-radius: 8px;
  border: 1px solid #eeeeee;
  height: 46px;
  width: 46px;
  object-fit: contain;
`;

const ButtonContainer = styled.div`
  flex-basis: 56px;
  flex-shrink: 0;
  min-height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const QuickChangeButton = styled(Button)`
  height: 22px;
  width: 22px;
  background: #eeeeee;
  max-width: 22px;
  min-width: 22px;
  padding: 0px;
  border-radius: 5px;

  svg {
    color: ${(props) => (props?.$isDisabled ? "gainsboro" : "slategrey")};
  }

  &:hover {
    background: lightgray;

    svg {
      color: black;
    }
  }
`;

const ButtonPlaceholder = styled.div`
  height: 22px;
  width: 22px;
  background: transparent;
  background-color: #eeeeee80;
  border-radius: 6px;
`;

const ImagePlaceHolder = (
  <div
    style={{
      height: 46,
      width: 46,
      borderRadius: 8,
      backgroundColor: "#eeeeee",
      border: "1px solid lightgray",
    }}
  />
);

const Seperator = () => (
  <div style={{ width: 1, height: 12, background: "#eeeeee" }} />
);

const MarketplaceChip = () => (
  <Tooltip
    title="Module was added from the marketplace"
    arrow
    placement="right"
  >
    <MpPointerContainer>
      <span style={{ color: "#813eee", lineHeight: 1 }}>MP</span>
    </MpPointerContainer>
  </Tooltip>
);

const CustomChip = () => (
  <Tooltip title="Custom module made by your team" arrow placement="right">
    <CmPointerContainer>
      <span style={{ color: "#337ab7", fontWeight: 600, lineHeight: 1 }}>
        CUSTOM
      </span>
    </CmPointerContainer>
  </Tooltip>
);

const shouldEditButtonDisplay = (
  moduleState,
  isManager,
  isMarketplaceModule,
) => {
  if (isMarketplaceModule) return false;
  if (isManager) return false;
  if (
    moduleState === MODULE_STATES.CLOSED ||
    moduleState === MODULE_STATES.IN_REVIEW ||
    moduleState === MODULE_STATES.LIVE
  ) {
    return false;
  }

  return true;
};

const shouldPreviewButtonDisplay = (moduleState, isManager) => {
  if (isManager) return false;

  if (moduleState === MODULE_STATES.IN_DEVELOPMENT) {
    return false;
  }
  return true;
};

const LibraryModuleDisplay = ({
  srcString,
  moduleName,
  marketplaceParentInfo = null,
}) => {
  const config = useGlobalStore((state) => state.config);
  const companyName = config?.current_company?.name;
  const isRetail = useGlobalStore((state) => state.isRetail);

  if (!isRetail) {
    return (
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <ModuleImage
          src={getSrcTwo(srcString)}
          effect="opacity"
          placeholder={ImagePlaceHolder}
          onError={(event) => (event.target.src = NoImage)}
        />
        <span
          style={{
            maxWidth: "26ch",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 500,
          }}
        >
          {moduleName}
        </span>
      </div>
    );
  }

  const isMarketplaceModule = Boolean(marketplaceParentInfo);

  return (
    <Wrapper>
      <ModuleImage
        src={getSrcTwo(srcString)}
        effect="opacity"
        placeholder={ImagePlaceHolder}
        onError={(event) => (event.target.src = NoImage)}
      />
      <TextContainer>
        <Tooltip
          style={{ width: "fit-content" }}
          title={moduleName}
          arrow
          placement="top"
        >
          <TitleSpan>{moduleName || "---/---"}</TitleSpan>
        </Tooltip>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <ParentSpan>
            {isMarketplaceModule
              ? marketplaceParentInfo?.companyName
              : companyName}
          </ParentSpan>
          <Seperator />
          {isMarketplaceModule ? <MarketplaceChip /> : <CustomChip />}
        </div>
      </TextContainer>
    </Wrapper>
  );
};

export default LibraryModuleDisplay;
